// In order to render second level for certain item pass data in the next format:
//   {
//    id: 34, label: 'Item Name',
//    value: [
//      { value:"all_games", label:"All Games", id: 4 },
//      { value:"top_games", label:"Top Games", id: 5 },
//      ]
//   }

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-dropdown-select';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { isArray, find, isEmpty, compact, debounce } from 'lodash-es';

import axios from '../configs/axios';
import StoreIcon from './StoreIcon';
import LoadingSpinner from './LoadingSpinner';
import { ErrorMessage } from './TypeaheadItems';
import helpers from '../utils/helpers';

const { CancelToken } = axios;

const inputSetAttributes = (searchInputEl) => {
  const attrs = {
    autocomplete: 'nope',
    'data-lpignore': 'true',
  };

  Object.keys(attrs).forEach((key) => {
    searchInputEl.setAttribute(key, attrs[key]);
  });
};

const childrenFormDefault = (option, removeItem, onItemRemove, desc) => {
  return (
    <div
      key={option.id}
      className="d-inline-block text-blue l-h-xl my-xxxs mx-xxs"
      style={{
        borderRadius: 16,
        border: '1px solid #3f89ff',
        paddingLeft: 12,
        paddingRight: 8,
      }}
    >
      {desc ? option.label.split('%')[0] : option.label}
      <span
        className="ml-sm cursor-pointer text-quiet text-xs"
        onClick={(e) => {
          removeItem && removeItem(e, option);
          onItemRemove && onItemRemove(e, option);
        }}
      >
        <i className="hicon-close v-a-m" style={{ marginBottom: 2 }} />
      </span>
    </div>
  );
};

class MultiLevelSelectTypeahead extends Component {
  static propTypes = {
    dataset: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
        disabled: PropTypes.bool,
      })
    ),
    selection: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    styles: PropTypes.object,
    selectId: PropTypes.string.isRequired,
    requestPath: PropTypes.string,
    placeholder: PropTypes.string,
    dropdownHeight: PropTypes.string,
    selectStyle: PropTypes.object,
    emptyState: PropTypes.element,
    childrenContainerHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    childrenContainerWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    keepOpen: PropTypes.bool,
    disabled: PropTypes.bool,
    clearable: PropTypes.bool,
    clearOnSelect: PropTypes.bool,
    closeOnSelect: PropTypes.bool,
    multi: PropTypes.bool,
    withStoreIcon: PropTypes.bool,
    minLength: PropTypes.number,
    selectedOptionsClassName: PropTypes.string,
    dropdownPosition: PropTypes.oneOf(['auto', 'top', 'bottom']),
    nestedGroupPosition: PropTypes.oneOf(['left', 'right']),
    showAsDropdown: PropTypes.bool,
    store: PropTypes.string,
    staticSelectedOptions: PropTypes.node,
    withResponseItemDescription: PropTypes.bool,
    inlineSelected: PropTypes.bool, // shows list of selected items inline with search;
    requestNewUserState: PropTypes.oneOf(['requestNewUserButton', 'addNewUser']),
    filterMenuOptionsBySearch: PropTypes.bool,
    showSelectedOptions: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    itemValidationFunc: PropTypes.func,
    responseDecorator: PropTypes.func,
    onItemRemove: PropTypes.func,
    childrenForm: PropTypes.func,
  };

  static defaultProps = {
    emptyState: <p className="text-center py-sm">No items found by this criteria.</p>,
    childrenForm: childrenFormDefault,
    childrenContainerHeight: 200,
    childrenContainerWidth: 300,
    disabled: false,
    selection: [],
    styles: {},
    selectStyle: {},
    clearable: false,
    clearOnSelect: false,
    placeholder: 'Enter a query',
    responseDecorator: (data) => data,
    minLength: 0,
    multi: true,
    withStoreIcon: false,
    dropdownPosition: 'auto',
    nestedGroupPosition: 'right',
    withResponseItemDescription: false,
    inlineSelected: false,
    requestNewUserState: 'requestNewUserButton',
    itemValidationFunc: () => {},
    filterMenuOptionsBySearch: true,
    showSelectedOptions: true,
  };

  static getDerivedStateFromProps = (props) => {
    const newState = {};
    if (Array.isArray(props.dataset)) {
      newState.options = props.dataset;
    }
    return newState;
  };

  constructor(props) {
    super(props);
    this.state = {
      options: props.dataset || [],
      activeSubMenuId: 0,
      subMenuTopCoordinate: 0,
      isLoading: false,
      isOpen: false,
      errorOccurred: false,
      inputValue: '',
      displayInput: true,
    };
    this.cancel = null;
    this.searchInputRef = React.createRef();
  }
  componentDidMount() {
    inputSetAttributes(this.searchInputRef.current);
  }

  delay = debounce(() => {
    this.handleInputSearch();
  }, 500);

  handleValueSearch = (e) => {
    const inputValue = e.target.value;
    this.delay.cancel();
    if (this.cancel) this.cancel();
    this.setState({ inputValue, isLoading: true }, () => {
      this.delay();
    });
  };

  handleChange = (selectedOptions) => {
    const { onChange, clearOnSelect } = this.props;
    onChange(selectedOptions);
    if (clearOnSelect && this.state.isOpen) {
      this.searchInputRef.current.focus();
    }
  };

  handleInputKeydown = ({ key, target: { value } }, inputState) => {
    const { requestNewUserState, selection, itemValidationFunc } = this.props;
    const isValidEmail = helpers.validateEmail(value);
    if (key === 'Enter' && requestNewUserState === 'addNewUser' && isValidEmail) {
      this.handleChange([
        ...selection,
        {
          id: value, // DEVNOTE it should to be re-set on backend once request sent
          newUser: true,
          kind: 'user',
          text: value,
          label: value,
          invalid: itemValidationFunc(value),
          value,
        },
      ]);
      inputState.search = '';
    }
  };

  customOptionRenderer = ({ item, props, state, methods }) => {
    const label = item.label.split('(')[0];
    return (
      <div
        key={item.id}
        className={cx('text-truncate selected-item', { 'item-invalid': item.invalid })} // DEVNOTE to add invalid key property type bool
      >
        <i className="hicon-profile-2 mr-xs" />
        <span className="delete-btn cursor-pointer" onClick={(e) => methods.removeItem(e, item)}>
          <i className="hicon-close text-16" />
        </span>
        <span className="selected-item-label">{label.length > 1 ? label : item.value}</span>
      </div>
    );
  };

  customContentRenderer = ({ props, state, methods, inputRef }) => {
    const { multi, selection, placeholder, withStoreIcon, showAsDropdown, inlineSelected } =
      this.props;
    const { displayInput } = this.state;
    this.searchInputRef = inlineSelected ? inputRef : this.searchInputRef;
    const input = (
      <div className={cx('d-flex align-items-center flex-grow', showAsDropdown ? 'dropdown' : '')}>
        {multi && <i className="hicon-search-short mr-xxs ml-xxs" />}
        {!multi && !displayInput && <i className="hicon-search-short mr-xxs ml-xxs" />}
        <input
          type="text"
          value={state.search}
          className={cx('text-xxs react-dropdown-input', {
            'react-dropdown-single': !multi,
            'd-none': !multi && displayInput,
          })}
          placeholder={placeholder}
          onFocus={this.handleInputSearch}
          onKeyDown={(e) => this.handleInputKeydown(e, state)}
          onChange={(e) => {
            methods.setSearch(e);
            this.handleValueSearch(e);
          }}
          ref={this.searchInputRef}
        />
        {!multi && !isEmpty(selection) && displayInput && (
          <div
            className={cx(
              'px-xs selected-single-category d-flex align-items-center',
              showAsDropdown ? 'btn-dropdown' : ''
            )}
          >
            {withStoreIcon && selection[0].store && (
              <div className="mr-xxs flex-no-shrink">
                <StoreIcon store={selection[0].store} />
              </div>
            )}
            {selection[0].label}
          </div>
        )}
      </div>
    );
    return input;
  };

  dropdownEnabled = () =>
    this.selectRef && this.selectRef.state.search.length >= this.props.minLength;

  handleInputSearch = () => {
    const { requestPath, responseDecorator } = this.props;
    const { inputValue } = this.state;
    if (!requestPath) {
      this.setState({ isLoading: false });
      return;
    }
    if (!this.dropdownEnabled()) {
      return;
    }
    this.setState({ isLoading: true, errorOccurred: false }, () => {
      axios
        .get(requestPath, {
          params: {
            term: inputValue,
          },
          cancelToken: new CancelToken((c) => (this.cancel = c)),
        })
        .then(({ data }) => {
          this.setState({ options: responseDecorator(data), isLoading: false });
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            this.setState({ isLoading: false, errorOccurred: true, options: [] });
          }
        });
    });
  };

  handleSubMenuClick = (item) => {
    const itemTop = this[`listItem-${item.id}`]?.getBoundingClientRect()?.top;
    const itemParent = this[`listItem-${item.id}`]?.offsetParent?.getBoundingClientRect();
    const isOpenToTop = this[`listItem-${item.id}`]?.offsetParent?.offsetParent?.classList.contains(
      'react-dropdown-select-dropdown-position-top'
    );
    const itemOffsetTop = !isOpenToTop
      ? itemTop - (itemParent?.top || 0) + 35
      : itemTop - (itemParent?.top || 0) - (itemParent?.height || 0);
    this.setState((state) => ({
      activeSubMenuId: state.activeSubMenuId === item.id ? 0 : item.id,
      subMenuTopCoordinate: itemOffsetTop,
    }));
  };

  handleOnListItemClick = (e, item, methods) => {
    e.preventDefault();
    e.stopPropagation();
    methods.addItem(item);
  };

  handleDropdownOpen = () => {
    const displayInput =
      !this.props.multi && !this.props.showAsDropdown
        ? { displayInput: !this.state.displayInput }
        : {};
    this.setState({ isOpen: true, ...displayInput });
  };

  handleDropdownClose = () => {
    const displayInput =
      !this.props.multi && !this.props.showAsDropdown
        ? { displayInput: !this.state.displayInput }
        : {};
    this.setState({ isOpen: false, activeSubMenuId: 0, ...displayInput });
  };

  handleSearchFilter = (el, query) => {
    if (isArray(el.value)) {
      const filteredNestedArray = el.value.filter(
        (subEl) => subEl.label.toLowerCase().indexOf(query.toLowerCase()) !== -1
      );
      if (isEmpty(filteredNestedArray)) return false;
      return { ...el, value: filteredNestedArray };
    }
    if (el.label.toLowerCase().indexOf(query.toLowerCase()) !== -1) return el;
    return false;
  };

  isSelected = (state, el) => find(state.values, (val) => val.id.toString() === el.id.toString());

  customDropdownRenderer = ({ state, methods }) => {
    const { options, isLoading, errorOccurred } = this.state;
    const {
      emptyState,
      withStoreIcon,
      multi,
      withResponseItemDescription,
      requestNewUserState,
      filterMenuOptionsBySearch,
    } = this.props;
    const containerToInsert = this.selectTypeahed().select.current;
    const items = filterMenuOptionsBySearch
      ? compact(options.map((el) => this.handleSearchFilter(el, state.search)))
      : options;
    return (
      <ul className="pos-rel show-scrollbar">
        {Boolean(items.length) &&
          !isLoading &&
          items.map((item) => {
            const withSubItems = isArray(item.value);
            return (
              <li
                key={item.id}
                className={cx('react-dropdown-item', {
                  selected: this.isSelected(state, item),
                  'sub-menu-selected': multi && this.state.activeSubMenuId === item.id,
                  'group-item': withSubItems,
                  disabled: item.disabled,
                  'whith-descrition': withResponseItemDescription,
                })}
                ref={(ref) => (this[`listItem-${item.id}`] = ref)}
                onClick={
                  withSubItems
                    ? () => this.handleSubMenuClick(item)
                    : (e) => this.handleOnListItemClick(e, item, methods)
                }
              >
                <div className="d-flex align-items-center flex-grow w-100">
                  {withStoreIcon && item.store && (
                    <div className="mr-xxs flex-no-shrink">
                      <StoreIcon store={item.store} />
                    </div>
                  )}
                  <span className="d-inline-block pr-xxs text-truncate">
                    {withResponseItemDescription ? item.label.split('%')[0] : item.label}
                  </span>
                  {withSubItems && <i className="hicon-angle-right text-quiet ml-auto text-xs" />}
                </div>
                {withResponseItemDescription && (
                  <div className="d-flex item-description w-100">{item.description}</div>
                )}
                {withSubItems &&
                  ReactDOM.createPortal(
                    this.renderNestedGroup(item, methods, state),
                    containerToInsert
                  )}
              </li>
            );
          })}
        {!items.length && !isLoading && !errorOccurred && requestNewUserState !== 'addNewUser' && (
          <li>{emptyState}</li>
        )}
        {isLoading && <li>{this.renderLoadingState()}</li>}
        {errorOccurred && <ErrorMessage />}
      </ul>
    );
  };

  selectTypeahed = () => this.selectRef;

  renderLoadingState = () => (
    <div className="py-sm d-flex align-items-center justify-content-center text-quiet">
      <LoadingSpinner size="sm" className="mr-xxs" />
      <p className="text-center m-b-0">Loading...</p>
    </div>
  );

  renderNestedGroup = (item, methods, state) => (
    <ul
      className={cx(
        'nested-group show-scrollbar',
        `nested-group-${this.props.nestedGroupPosition}`,
        { hidden: this.state.activeSubMenuId !== item.id }
      )}
      style={{ top: this.state.subMenuTopCoordinate }}
    >
      {item.value.map((el) => (
        <li
          key={el.id}
          onClick={(e) => this.handleOnListItemClick(e, el, methods)}
          className={cx('react-dropdown-item nested', {
            selected: this.isSelected(state, el),
            disabled: el.disabled,
          })}
        >
          <div className="d-flex align-items-center flex-grow w-100">
            {this.props.withStoreIcon && el.store && (
              <div className="mr-xxs flex-no-shrink">
                <StoreIcon store={el.store} />
              </div>
            )}
            <span className="d-inline-block pr-xxs text-truncate">{el.label}</span>
          </div>
        </li>
      ))}
    </ul>
  );

  renderSelectedOptions = () => (
    <div
      className={cx(
        'selected-options show-scrollbar',
        this.props.selectedOptionsClassName,
        { 'mt-md': this.props.selection.length >= 1 },
        { hidden: this.props.inlineSelected && this.props.selection.length < 1 }
      )}
      style={{
        maxHeight: this.props.childrenContainerHeight,
        maxWidth: this.props.childrenContainerWidth,
      }}
    >
      {this.props.staticSelectedOptions}
      {this.props.selection &&
        this.props.selection.map((option) =>
          this.props.childrenForm(
            option,
            this.selectRef && this.selectRef.removeItem,
            this.props.onItemRemove,
            this.props.withResponseItemDescription
          )
        )}
    </div>
  );

  renderInputCleaner = ({ props, state, methods }) => {
    const flushInput = () => {
      const e = { target: { value: '' } };
      methods.setSearch(e);
      methods.dropDown();
      props.onDropdownClose();
      this.handleValueSearch(e);
    };
    if (state.search.length < 1) return '';
    return (
      <div
        className="react-dropdown-select-clear"
        tabIndex="-1"
        onClick={flushInput}
        onKeyPress={flushInput}
      >
        &times;
      </div>
    );
  };

  render() {
    const { options, isOpen } = this.state;
    const {
      selectId,
      selection,
      multi,
      keepOpen,
      disabled,
      clearable,
      clearOnSelect,
      closeOnSelect,
      dropdownHeight,
      selectStyle,
      styles,
      dropdownPosition,
      showAsDropdown,
      inlineSelected,
      requestNewUserState,
      showSelectedOptions,
    } = this.props;
    return (
      <div
        className={cx(
          'multi-level-select-search-container',
          { 'multi-level-select-open': isOpen },
          { 'show-as-dropdown': showAsDropdown },
          { 'inline-selected': inlineSelected }
        )}
        style={styles}
        id={selectId}
      >
        <Select
          multi={multi}
          dropdownHeight={dropdownHeight}
          values={selection}
          onChange={this.handleChange}
          options={options}
          dropdownGap={-2}
          ref={(ref) => {
            this.selectRef = ref;
          }}
          clearOnBlur={false}
          contentRenderer={!inlineSelected && this.customContentRenderer}
          inputRenderer={inlineSelected && this.customContentRenderer}
          optionRenderer={inlineSelected && this.customOptionRenderer}
          dropdownRenderer={this.customDropdownRenderer}
          className="multi-level-select-search"
          clearOnSelect={clearOnSelect}
          closeOnSelect={closeOnSelect}
          style={{ ...selectStyle }}
          clearRenderer={(c) => this.renderInputCleaner(c)}
          clearable={clearable}
          disabled={disabled}
          keepOpen={keepOpen}
          dropdownPosition={dropdownPosition}
          onDropdownOpen={this.handleDropdownOpen}
          onDropdownClose={this.handleDropdownClose}
          create={requestNewUserState === 'addNewUser'}
        />
        {multi && showSelectedOptions && this.renderSelectedOptions()}
      </div>
    );
  }
}

export default MultiLevelSelectTypeahead;
